import React, { useState } from 'react';
import Meta from 'src/components/Meta';
import { useGaEventPageView } from 'src/utils/gaEvent';
import styled from 'styled-components';
import { AssessmentCardSkeleton, PageContainer, Poppins, Spacer } from 'src/common';
import { ScenarioGroupCard } from './comps';
import { Tile, TileContainer, TileTitle } from 'src/components/Tile';
import { Link, useNavigate } from 'react-router-dom';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import { isQueryLoading } from 'src/graphql/client';
import { useAuth } from 'src/state/auth';
import { useScenarioGroups } from 'src/graphql';
import Button from 'src/components/form/Button';
import { AddScenarioPackModal } from './AddScenarioPackModal';
import { EditScenarioPackModal } from './EditScenarioPackModal';
import { PublishScenarioPackModal } from './PublishScenarioPackModal';

const ActionButton = styled(Button)`
  width: 340px;
  min-width: 340px;
  padding-left: 3rem;
  padding-right: 3rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 320px;
    min-width: 320px;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    width: 100%;
    min-width: 0;
  }
`;

const Div = styled(PageContainer)`
  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    .head {
      flex-direction: column;
      align-items: flex-start;
    }

    .actions {
      width: 100%;
      justify-content: flex-start;
    }
  }
`;

const ScenarioLibraryPage = () => {
  const navigate = useNavigate();
  const { workspaceId, isPlatformAdmin } = useAuth();
  const [isAddScenarioPackOpen, setIsAddScenarioPackOpen] = useState(false);
  const [isEditScenarioPackOpen, setIsEditScenarioPackOpen] = useState(false);
  const [isPublishScenarioPackOpen, setIsPublishScenarioPackOpen] = useState(false);
  useGaEventPageView('Cydea | Scenario Library');

  const { data, loading, networkStatus, refetch } = useScenarioGroups(workspaceId);
  const isLoading = isQueryLoading({ loading, data, networkStatus });

  return (
    <>
      <Meta title={`Cydea | Scenario Library`} feedbackScript />
      <Div>
        <div className="head">
          <Poppins px={32} weight={600}>
            Scenario Library
          </Poppins>
          {isPlatformAdmin && (
            <div className="actions">
              <ActionButton
                secondary
                onClick={() => setIsEditScenarioPackOpen(true)}
                data-cy="open-edit-scenario-pack-modal"
              >
                Edit Scenario Pack
              </ActionButton>
              <ActionButton
                secondary
                onClick={() => setIsPublishScenarioPackOpen(true)}
                data-cy="open-publish-scenario-pack-modal"
              >
                Publish Scenario Pack
              </ActionButton>
              <ActionButton
                primary
                onClick={() => setIsAddScenarioPackOpen(true)}
                data-cy="open-add-scenario-pack-modal"
              >
                Add Scenario Pack
              </ActionButton>
            </div>
          )}
        </div>
        <Spacer $px={20} />
        <TileContainer>
          {!isLoading ? (
            <>
              <Tile
                className="hover-shadow"
                data-cy="all-scenarios-card"
                onClick={() => {
                  navigate('/scenario-library/all');
                  mpEvent(MPEvents.Link, {
                    source: {
                      value: ['Scenario Library'],
                    },
                    destination: { value: ['Scenario Library'] },
                    tags: ['SCENARIO'],
                  });
                }}
              >
                <Link
                  to="/scenario-library/all"
                  onClick={(e) => {
                    e.stopPropagation();
                    mpEvent(MPEvents.Link, {
                      source: {
                        value: ['Scenario Library'],
                      },
                      destination: { value: ['Scenario Library'] },
                      tags: ['SCENARIO'],
                    });
                  }}
                >
                  <TileTitle>All Scenarios</TileTitle>
                </Link>
              </Tile>
              {data?.scenarioGroups.map((group) => (
                <ScenarioGroupCard
                  key={group.id}
                  group={group}
                />
              ))}
            </>
          ) : (
            <>
              <AssessmentCardSkeleton />
              <AssessmentCardSkeleton />
              <AssessmentCardSkeleton />
            </>
          )}
        </TileContainer>
        {isPlatformAdmin && isAddScenarioPackOpen && (
          <AddScenarioPackModal
            isOpen={isAddScenarioPackOpen}
            onClose={() => setIsAddScenarioPackOpen(false)}
            onCreated={async () => {
              await refetch();
            }}
          />
        )}
        {isPlatformAdmin && isEditScenarioPackOpen && (
          <EditScenarioPackModal
            isOpen={isEditScenarioPackOpen}
            onClose={() => setIsEditScenarioPackOpen(false)}
            groups={data?.scenarioGroups || []}
            onEdited={async () => {
              await refetch();
            }}
          />
        )}
        {isPlatformAdmin && isPublishScenarioPackOpen && (
          <PublishScenarioPackModal
            isOpen={isPublishScenarioPackOpen}
            onClose={() => setIsPublishScenarioPackOpen(false)}
            groups={data?.scenarioGroups || []}
            onPublished={async () => {
              await refetch();
            }}
          />
        )}
      </Div>
    </>
  );
};

export default ScenarioLibraryPage;
